import { useState } from "react";
import { ComplianceBadge } from "@/components/ui/compliance-badge";
import { Button } from "@/components/ui/button";
import type { ComplianceScore } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import { RefreshCw, AlertTriangle, AlertCircle, ShieldCheck, Loader2, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const tierLabels: Record<string, string> = {
  pass: "Pass",
  advisory: "Advisory",
  "critical-fail": "Critical Fail",
};

function dimensionLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

interface IssueCardProps {
  issue: { code: string; message: string; dimension: string; suggestion?: string };
  variant: "critical" | "advisory";
}

function IssueCard({ issue, variant }: IssueCardProps) {
  const isCritical = variant === "critical";
  return (
    <div
      className={cn(
        "rounded-lg border p-3 space-y-1",
        isCritical
          ? "border-red-800/30 bg-red-900/[0.06]"
          : "border-yellow-800/30 bg-yellow-900/[0.06]"
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "font-mono text-xs font-medium",
            isCritical ? "text-red-400" : "text-yellow-400"
          )}
        >
          {issue.code}
        </span>
        <span className="text-xs text-muted-foreground">{dimensionLabel(issue.dimension)}</span>
      </div>
      <p className="text-sm">{issue.message}</p>
      {issue.suggestion && (
        <p className="text-xs text-muted-foreground italic">{issue.suggestion}</p>
      )}
    </div>
  );
}

export interface ComplianceDetailSectionProps {
  score: ComplianceScore | null | undefined;
  onReaudit?: () => void;
  isAuditing?: boolean;
  defaultExpanded?: boolean;
}

export function ComplianceDetailSection({
  score,
  onReaudit,
  isAuditing,
  defaultExpanded = false,
}: ComplianceDetailSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Null / not-yet-audited state
  if (!score) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Not yet audited</span>
        </div>
        {onReaudit && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReaudit}
            disabled={isAuditing}
          >
            {isAuditing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Auditing…
              </>
            ) : (
              "Audit Now"
            )}
          </Button>
        )}
      </div>
    );
  }

  const dimensionEntries = Object.entries(score.dimensions);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      {/* Header: toggle + re-audit as siblings to avoid nested buttons */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-3 min-w-0 cursor-pointer select-none text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="compliance-detail-content"
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
              isExpanded && "rotate-90"
            )}
          />
          <ComplianceBadge score={score} showScore className="text-sm" />
          <span className="text-sm text-muted-foreground truncate">
            {score.overall}/100 — {tierLabels[score.tier] ?? score.tier}
          </span>
        </button>
        {onReaudit && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 ml-2"
            onClick={onReaudit}
            disabled={isAuditing}
            aria-label="Re-audit compliance"
          >
            {isAuditing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            <span className="ml-1.5 hidden sm:inline">Re-audit</span>
          </Button>
        )}
      </div>

      {/* Collapsible content with smooth grid-rows transition */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        aria-hidden={!isExpanded}
        id="compliance-detail-content"
      >
        <div className="overflow-hidden">
          <div className="space-y-4 pt-4">
            {/* Dimension progress bars */}
            {dimensionEntries.length > 0 && (
              <div className="space-y-2">
                {dimensionEntries.map(([dim, val]) => (
                  <div key={dim} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{dimensionLabel(dim)}</span>
                      <span className="tabular-nums font-medium">{val}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          val >= 80
                            ? "bg-green-500/70"
                            : val >= 50
                              ? "bg-yellow-500/70"
                              : "bg-red-500/70"
                        )}
                        style={{ width: `${Math.min(100, Math.max(0, val))}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Critical issues */}
            {score.criticalCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-red-400">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Critical Issues ({score.criticalCount})
                </div>
                {score.issues
                  .filter((i) => i.tier === "critical")
                  .map((issue) => (
                    <IssueCard key={issue.code} issue={issue} variant="critical" />
                  ))}
              </div>
            )}

            {/* Advisory issues */}
            {score.advisoryCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-yellow-400">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Advisory Issues ({score.advisoryCount})
                </div>
                {score.issues
                  .filter((i) => i.tier === "advisory")
                  .map((issue) => (
                    <IssueCard key={issue.code} issue={issue} variant="advisory" />
                  ))}
              </div>
            )}

            {/* Footer: last audited */}
            <div className="text-xs text-muted-foreground pt-1 border-t border-white/[0.06]">
              Last audited {formatDistanceToNow(new Date(score.auditedAt), { addSuffix: true })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
